import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, CalendarDays, RefreshCw, HeartHandshake, IceCream, Star, CheckCircle2, Check, ArrowRight } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
const bonuses = [{
  id: 1,
  icon: ShoppingCart,
  title: 'Lista de Compras Hacker',
  value: '$3.500',
  desc: 'No pierdas horas en el supermercado. Entrá y salí del súper en 15 minutos con los ingredientes exactos para todas tus recetas.',
  color: 'bg-emerald-50',
  iconColor: 'text-emerald-500',
  borderColor: 'border-emerald-200'
}, {
  id: 2,
  icon: CalendarDays,
  title: 'Menú Semanal Imprimible',
  value: '$2.900',
  desc: 'Ya no tendrás que pensar todos los días qué cocinar. Pegalo en tu heladera y olvidate. Nutrición automática para toda la semana.',
  color: 'bg-orange-50',
  iconColor: 'text-orange-500',
  borderColor: 'border-orange-200'
}, {
  id: 3,
  icon: RefreshCw,
  title: 'Guía de Sustitutos Invisibles',
  value: '$4.500',
  desc: '¿A tu hijo no le gusta una cierta verdura? Usá esta guía para saber con qué otra verdura podés reemplazarla y lograr igual esa receta.',
  color: 'bg-amber-50',
  iconColor: 'text-amber-500',
  borderColor: 'border-amber-200'
}, {
  id: 4,
  icon: HeartHandshake,
  title: 'PDF Cero Berrinches en la Mesa',
  value: '$7.900',
  desc: '5 técnicas de psicología inversa para desarmar el drama y que tu hijo se siente a comer con curiosidad.',
  color: 'bg-rose-50',
  iconColor: 'text-rose-500',
  borderColor: 'border-rose-200'
}, {
  id: 5,
  icon: IceCream,
  title: 'Postres con Superpoderes',
  value: '$5.500',
  desc: 'Brownies de legumbres y helados de palta. Nutrición total incluso en el momento del postre.',
  color: 'bg-violet-50',
  iconColor: 'text-violet-500',
  borderColor: 'border-violet-200'
}];
export function PricingSection() {
  const INITIAL_SECONDS = 30 * 60; // 30 minutes
  const [timeLeft, setTimeLeft] = useState(INITIAL_SECONDS);
  const [expired, setExpired] = useState(false);
  const [selectedBumps, setSelectedBumps] = useState({
    bump1: false,
    bump2: false,
    bump3: false
  });
  const isPackSelected = selectedBumps.bump1 && selectedBumps.bump2 && selectedBumps.bump3;
  const extrasTotal = isPackSelected ? 5900 : (selectedBumps.bump1 ? 2900 : 0) + (selectedBumps.bump2 ? 2900 : 0) + (selectedBumps.bump3 ? 2900 : 0);
  const finalTotal = 14900 + extrasTotal;
  useEffect(() => {
    if (timeLeft <= 0) {
      setExpired(true);
      return;
    }
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const isUrgent = timeLeft <= 5 * 60; // last 5 minutes
  return <section id="comprar-ahora" className="pt-16 pb-20 bg-gradient-to-br from-violet-700 via-violet-600 to-purple-700 relative overflow-hidden font-sans" data-id="element-266">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" data-id="element-267">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-emerald-100 blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-268" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-orange-200 blur-3xl translate-x-1/3 translate-y-1/3" data-id="element-269" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-5xl" data-id="element-270">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12 max-w-3xl mx-auto" data-id="element-271">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-272">
            🎁{' '}
            <span className="text-orange-400" data-id="element-273">
              ¡Aprovechá el 50% OFF y los 5 bonos de regalo!
            </span>
            <br data-id="element-274" />
            Llévate el Sistema Completo para que tu hijo coma sano{' '}
            <span className="text-yellow-300" data-id="element-275">(y vos recuperes la paz)</span>
          </h2>
          <p className="text-lg md:text-xl text-white/90 font-medium bg-white/15 backdrop-blur-sm inline-block px-6 py-3 rounded-2xl shadow-sm border border-white/20" data-id="element-276">
            Si te llevas 'Recetas Mágicas'{' '}
            <span className="font-bold text-orange-300" data-id="element-277">HOY</span>, desbloqueas
            estos 5 bonos valorados en{' '}
            <span className="line-through text-white/50" data-id="element-278">$24.300</span>{' '}
            totalmente{' '}
            <span className="font-black text-yellow-300" data-id="element-279">¡GRATIS!</span>
          </p>
        </motion.div>

        {/* Bonus Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-280">
          {bonuses.map((bonus, index) => {
          const Icon = bonus.icon;
          // Make the last two items span differently on tablet/desktop to center them nicely
          const isLastTwo = index >= 3;
          const lgSpanClass = isLastTwo && bonuses.length === 5 ? 'lg:col-span-1 lg:col-start-auto' : '';
          const mdSpanClass = isLastTwo && bonuses.length === 5 && index === 4 ? 'md:col-span-2 lg:col-span-1' : '';
          return <motion.div key={bonus.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`bg-white rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow border-2 ${bonus.borderColor} flex flex-col h-full relative overflow-hidden ${lgSpanClass} ${mdSpanClass}`} data-id="element-281">
                {/* Decorative background blob */}
                <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full ${bonus.color} opacity-50 blur-xl`} data-id="element-282" />

                <div className="flex items-start gap-4 mb-4 relative z-10" data-id="element-283">
                  <div className={`p-3 rounded-2xl ${bonus.color} ${bonus.iconColor} shrink-0`} data-id="element-284">
                    <Icon className="w-8 h-8" strokeWidth={2.5} data-id="element-285" />
                  </div>
                  <div data-id="element-286">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-287">
                      Bono #{bonus.id}
                    </div>
                    <h3 className="font-extrabold text-gray-900 text-lg leading-tight" data-id="element-288">
                      {bonus.title}
                    </h3>
                  </div>
                </div>

                <div className="mb-4 relative z-10" data-id="element-289">
                  <span className="text-sm text-gray-500 font-medium" data-id="element-290">
                    Valor:{' '}
                  </span>
                  <span className="text-sm text-gray-400 line-through font-bold mr-2" data-id="element-291">
                    {bonus.value}
                  </span>
                  <span className="text-sm font-black text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-md" data-id="element-292">
                    GRATIS
                  </span>
                </div>

                <p className="text-gray-600 text-sm leading-relaxed mt-auto relative z-10" data-id="element-293">
                  {bonus.desc}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Checkout Area */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="max-w-2xl mx-auto bg-white rounded-3xl p-8 md:p-10 shadow-xl border-4 border-emerald-100 text-center relative" data-id="element-294">
          {/* Urgency label */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm uppercase tracking-widest px-6 py-1.5 rounded-full shadow-md whitespace-nowrap" data-id="element-295">
            OFERTA POR TIEMPO LIMITADO
          </div>

          {/* Countdown */}
          {!expired ? <div className="flex justify-center gap-4 md:gap-8 mb-8 mt-4" data-id="element-296">
              {[{
            value: pad(hours),
            label: 'HORAS'
          }, {
            value: pad(minutes),
            label: 'MINUTOS'
          }, {
            value: pad(seconds),
            label: 'SEGUNDOS'
          }].map(({
            value,
            label
          }) => <div key={label} className="flex flex-col items-center" data-id="element-297">
                  <motion.span key={value} initial={{
              scale: 1.1,
              opacity: 0.8
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.2
            }} className={`text-4xl md:text-5xl font-black tabular-nums ${isUrgent ? 'text-red-500' : 'text-gray-800'}`} data-id="element-298">
                    {value}
                  </motion.span>
                  <span className="text-gray-500 text-xs font-bold tracking-widest mt-1" data-id="element-299">
                    {label}
                  </span>
                </div>)}
            </div> : <p className="text-red-500 font-extrabold text-xl mb-8 mt-4" data-id="element-300">
              ⏰ ¡La oferta expiró!
            </p>}

          {/* Scarcity */}
          <p className="text-orange-600 font-bold text-sm mb-8 flex items-center justify-center gap-2" data-id="element-301">
            <span className="relative flex h-3 w-3" data-id="element-302">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" data-id="element-303"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500" data-id="element-304"></span>
            </span>
            Solo quedan 5 copias con descuento
          </p>

          {/* CTA Button */}
          <motion.button onClick={() => {
          trackAddToCart();
          const key = `${selectedBumps.bump1 ? '1' : ''}${selectedBumps.bump2 ? '2' : ''}${selectedBumps.bump3 ? '3' : ''}`;
          const checkoutUrls: Record<string, string> = {
            '': 'https://cocinasimpleya.com/cart/53934251802908:1',
            '1': 'https://cocinasimpleya.com/cart/54065329701148:1',
            '2': 'https://cocinasimpleya.com/cart/54065333698844:1',
            '3': 'https://cocinasimpleya.com/cart/54065335140636:1',
            '12': 'https://cocinasimpleya.com/cart/54065338777884:1',
            '13': 'https://cocinasimpleya.com/cart/54065338941724:1',
            '23': 'https://cocinasimpleya.com/cart/54065339236636:1',
            '123': 'https://cocinasimpleya.com/cart/54065340743964:1'
          };
          window.open(checkoutUrls[key] || checkoutUrls[''], '_blank');
        }} whileHover={{
          scale: 1.05,
          boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.3), 0 10px 10px -5px rgba(16, 185, 129, 0.2)'
        }} whileTap={{
          scale: 0.98
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 25
        }} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xl md:text-2xl px-8 py-6 rounded-2xl shadow-lg cursor-pointer transition-colors flex flex-col items-center justify-center gap-1" data-id="element-305">
            <span data-id="element-306">
              <span className="line-through text-white/50 text-lg md:text-xl font-bold" data-id="element-307">
                $54.200
              </span>{' '}
              ¡QUIERO EL RECETARIO + LOS 5 BONOS POR $
              {finalTotal.toLocaleString('es-AR')}!
            </span>
            <span className="text-sm font-medium text-emerald-100 opacity-90" data-id="element-308">
              Acceso inmediato • Pago 100% seguro
            </span>
          </motion.button>

          {/* Order Bumps Section */}
          <div className="mt-10 text-left" data-id="element-309">
            <div className="bg-gradient-to-r from-orange-500 to-violet-600 rounded-2xl p-4 mb-6 text-center" data-id="element-310">
              <p className="text-white font-black text-lg md:text-xl tracking-tight" data-id="element-311">
                ⚡ ¡Asegurate resultados rápidos con estas ayudas extra! ⚡
              </p>
              <p className="text-orange-100 text-sm font-medium mt-1" data-id="element-312">
                👇 Hacé click para agregar a tu pedido 👇
              </p>
            </div>
            <div className="space-y-4 mb-6" data-id="element-313">
              {/* Bump 1 */}
              <div onClick={() => setSelectedBumps(prev => ({
              ...prev,
              bump1: !prev.bump1
            }))} className={`bump-flash flex items-start gap-3 p-5 rounded-2xl border-3 border-t-4 border-t-violet-500 cursor-pointer transition-all shadow-sm hover:shadow-md ${selectedBumps.bump1 && !isPackSelected ? 'border-emerald-500 border-t-violet-500 bg-emerald-50 shadow-emerald-100' : 'border-orange-300 hover:border-orange-400 bg-white'}`} data-id="element-314">
                <div className={`mt-0.5 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-all ${selectedBumps.bump1 ? 'bg-emerald-500 border-emerald-500 scale-110' : 'border-orange-400 bg-orange-50'}`} data-id="element-315">
                  {selectedBumps.bump1 ? <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-316" /> : <span className="bump-arrow text-orange-500 text-xs font-black" data-id="element-317">
                      +
                    </span>}
                </div>
                <div className="flex-1 -mt-5 -mr-5" data-id="element-318">
                  <div className="bg-violet-100 px-5 py-3 rounded-tr-2xl mb-3" data-id="element-319">
                    <span className="font-extrabold text-gray-900 text-sm md:text-base leading-snug" data-id="element-320">
                      📘 Cómo aplicar las recetas con un Sistema Diario para que
                      tu hijo coma verduras TODOS los días (sin rechazos)
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2 pr-5" data-id="element-321">
                    Para implementar todas las recetas, dejar de improvisar y
                    lograr que las verduras formen parte de la rutina diaria sin
                    peleas, rechazo ni estrés.
                  </p>
                  <span className="inline-block font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full text-sm border border-emerald-200" data-id="element-322">
                    +$2.900
                  </span>
                </div>
              </div>

              {/* Bump 2 */}
              <div onClick={() => setSelectedBumps(prev => ({
              ...prev,
              bump2: !prev.bump2
            }))} className={`bump-flash flex items-start gap-3 p-5 rounded-2xl border-3 border-t-4 border-t-orange-500 cursor-pointer transition-all shadow-sm hover:shadow-md ${selectedBumps.bump2 && !isPackSelected ? 'border-emerald-500 border-t-orange-500 bg-emerald-50 shadow-emerald-100' : 'border-orange-300 hover:border-orange-400 bg-white'}`} data-id="element-323">
                <div className={`mt-0.5 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-all ${selectedBumps.bump2 ? 'bg-emerald-500 border-emerald-500 scale-110' : 'border-orange-400 bg-orange-50'}`} data-id="element-324">
                  {selectedBumps.bump2 ? <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-325" /> : <span className="bump-arrow text-orange-500 text-xs font-black" data-id="element-326">
                      +
                    </span>}
                </div>
                <div className="flex-1 -mt-5 -mr-5" data-id="element-327">
                  <div className="bg-orange-100 px-5 py-3 rounded-tr-2xl mb-3" data-id="element-328">
                    <span className="font-extrabold text-gray-900 text-sm md:text-base leading-snug" data-id="element-329">
                      ⚡ Qué Método Express usar para resolver cualquier comida
                      en 10 minutos (aunque no tengas nada pensado)
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2 pr-5" data-id="element-330">
                    La solución ideal para esos días en los que no sabés qué
                    cocinar, no tuviste tiempo de pensar y necesitás resolver
                    rápido algo nutritivo.
                  </p>
                  <span className="inline-block font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full text-sm border border-emerald-200" data-id="element-331">
                    +$2.900
                  </span>
                </div>
              </div>

              {/* Bump 3 */}
              <div onClick={() => setSelectedBumps(prev => ({
              ...prev,
              bump3: !prev.bump3
            }))} className={`bump-flash flex items-start gap-3 p-5 rounded-2xl border-3 border-t-4 border-t-emerald-500 cursor-pointer transition-all shadow-sm hover:shadow-md ${selectedBumps.bump3 && !isPackSelected ? 'border-emerald-500 border-t-emerald-500 bg-emerald-50 shadow-emerald-100' : 'border-orange-300 hover:border-orange-400 bg-white'}`} data-id="element-332">
                <div className={`mt-0.5 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-all ${selectedBumps.bump3 ? 'bg-emerald-500 border-emerald-500 scale-110' : 'border-orange-400 bg-orange-50'}`} data-id="element-333">
                  {selectedBumps.bump3 ? <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-334" /> : <span className="bump-arrow text-orange-500 text-xs font-black" data-id="element-335">
                      +
                    </span>}
                </div>
                <div className="flex-1 -mt-5 -mr-5" data-id="element-336">
                  <div className="bg-emerald-100 px-5 py-3 rounded-tr-2xl mb-3" data-id="element-337">
                    <span className="font-extrabold text-gray-900 text-sm md:text-base leading-snug" data-id="element-338">
                      🥦 Evitá tirar comida: El Sistema Antidesperdicio para
                      organizar tu cocina y asegurar comidas saludables toda la
                      semana
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2 pr-5" data-id="element-339">
                    Aprendé a organizar mejor tus compras, aprovechar
                    ingredientes y tener comidas saludables listas sin
                    desperdiciar plata ni verduras.
                  </p>
                  <span className="inline-block font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full text-sm border border-emerald-200" data-id="element-340">
                    +$2.900
                  </span>
                </div>
              </div>

              {/* Pack Deal */}
              <div onClick={() => {
              const newState = !isPackSelected;
              setSelectedBumps({
                bump1: newState,
                bump2: newState,
                bump3: newState
              });
            }} className={`relative flex flex-col gap-3 p-5 rounded-2xl border-2 cursor-pointer transition-all mt-8 overflow-visible ${isPackSelected ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-orange-200 bg-orange-50/50 hover:border-orange-300'}`} data-id="element-341">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-black px-4 py-1 rounded-full tracking-widest shadow-sm whitespace-nowrap" data-id="element-342">
                  MEJOR VALOR
                </div>
                <div className="flex items-start gap-3" data-id="element-343">
                  <div className={`mt-1 shrink-0 w-6 h-6 rounded flex items-center justify-center border-2 ${isPackSelected ? 'bg-orange-500 border-orange-500' : 'border-orange-300 bg-white'}`} data-id="element-344">
                    {isPackSelected && <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-345" />}
                  </div>
                  <div data-id="element-346">
                    <div className="flex flex-wrap items-baseline gap-2 mb-1" data-id="element-347">
                      <span className="font-extrabold text-gray-900 text-base md:text-lg" data-id="element-348">
                        PACK IMPLEMENTACIÓN COMPLETA
                      </span>
                      <span className="font-black text-orange-600 whitespace-nowrap" data-id="element-349">
                        +$5.900
                      </span>
                      <span className="text-xs text-gray-400 line-through font-bold whitespace-nowrap" data-id="element-350">
                        +$8.700
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3" data-id="element-351">
                      Todo lo que necesitás para que las recetas no queden en
                      intención, sino que se conviertan en una solución real,
                      práctica y sostenible en tu casa.
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1.5 font-medium" data-id="element-352">
                      <li className="flex items-center gap-1.5" data-id="element-353">
                        <Check className="w-3.5 h-3.5 text-orange-500" data-id="element-354" /> El
                        Sistema Diario
                      </li>
                      <li className="flex items-center gap-1.5" data-id="element-355">
                        <Check className="w-3.5 h-3.5 text-orange-500" data-id="element-356" /> El
                        Método Express
                      </li>
                      <li className="flex items-center gap-1.5" data-id="element-357">
                        <Check className="w-3.5 h-3.5 text-orange-500" data-id="element-358" /> El
                        Sistema Antidesperdicio
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Total & Checkout Button */}
            <motion.div onClick={() => {
            trackAddToCart();
            // Determine checkout URL based on selected bumps
            const key = `${selectedBumps.bump1 ? '1' : ''}${selectedBumps.bump2 ? '2' : ''}${selectedBumps.bump3 ? '3' : ''}`;
            const checkoutUrls: Record<string, string> = {
              '': 'https://cocinasimpleya.com/cart/53934251802908:1',
              '1': 'https://cocinasimpleya.com/cart/54065329701148:1',
              '2': 'https://cocinasimpleya.com/cart/54065333698844:1',
              '3': 'https://cocinasimpleya.com/cart/54065335140636:1',
              '12': 'https://cocinasimpleya.com/cart/54065338777884:1',
              '13': 'https://cocinasimpleya.com/cart/54065338941724:1',
              '23': 'https://cocinasimpleya.com/cart/54065339236636:1',
              '123': 'https://cocinasimpleya.com/cart/54065340743964:1'
            };
            window.open(checkoutUrls[key] || checkoutUrls[''], '_blank');
          }} whileHover={{
            scale: 1.02,
            boxShadow: '0 0 30px rgba(16,185,129,0.4)'
          }} whileTap={{
            scale: 0.98
          }} className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-2xl p-5 text-center shadow-lg cursor-pointer transition-all hover:from-emerald-500 hover:to-emerald-400" data-id="element-359">
              <p className="text-sm text-emerald-100 mb-1 font-medium tracking-wide uppercase" data-id="element-360">
                Resumen de tu pedido
              </p>
              <p className="text-base md:text-lg font-medium text-emerald-50" data-id="element-361">
                Sistema Completo ($14.900){' '}
                {extrasTotal > 0 && <span className="text-yellow-300 font-bold" data-id="element-362">
                    + Extras (${extrasTotal.toLocaleString('es-AR')})
                  </span>}
              </p>
              <div className="text-3xl md:text-4xl font-black mt-2 text-white" data-id="element-363">
                TOTAL: ${finalTotal.toLocaleString('es-AR')}
              </div>
              <p className="text-emerald-100 font-bold text-sm mt-2 flex items-center justify-center gap-2" data-id="element-364">
                🛒 CLICK AQUÍ PARA COMPRAR <ArrowRight className="w-4 h-4" data-id="element-365" />
              </p>
            </motion.div>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-col items-center gap-3 mt-6" data-id="element-366">
            <div className="flex items-center justify-center gap-1 text-yellow-400" data-id="element-367">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-current" data-id="element-368" />)}
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm font-medium" data-id="element-369">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-370" />
              <span data-id="element-371">Garantía de satisfacción de 60 días</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}